/**
 * Staff queue for violations matched to a facility only through an EXPIRED EPA
 * or FRS id. Those rows are hidden from the customer and from the Airtable sync
 * until someone here decides, per match group, that the match is real.
 *
 * Staff-owned outcomes:
 *  - Accept — releases every violation held against the group, including ones
 *    that arrive in later weekly runs. Reversible; un-accepting re-holds them
 *    in-app, though it cannot retract a row already pushed to Amazon's Airtable.
 *  - Reject — dismisses the group as not this facility's. Nothing is released, so
 *    it is reversible; the rows stay hidden and leave the pending view.
 *  - Fix the ID — correct the expiry on the facility profile. The next run
 *    clears the hold, and the group drops off this queue on its own.
 *
 * The queue is derived, not stored: a group appears only while violations still
 * point at it.
 */
import { useMutation } from "@apollo/client";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ExpiredAltIdMatchReviewsQuery } from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import {
  EXPIRED_ALT_ID_MATCH_REVIEWS,
  SET_EXPIRED_ALT_ID_MATCH_ACCEPTED,
  SET_EXPIRED_ALT_ID_MATCH_REJECTED,
} from "hooks/useExpiredAltIdMatchReviews";
import { ReactNode, useCallback, useMemo, useState } from "react";

import { buildViolationMatchColumns, VIOLATION_MATCH_COLUMN_FIELDS } from "./columns";
import { ReviewDecision, ReviewRow } from "./reviewDisplay";
import { ViolationMatchDrawer } from "./ViolationMatchDrawer";

/** Toast copy per decision. */
function decisionMessage(decision: ReviewDecision, row: ReviewRow): string {
  const subject = `${row.violationCount} violation(s) for ${row.facilityName}`;
  switch (decision) {
    case "accept":
      return `Released ${subject}.`;
    case "unaccept":
      return `Re-held ${subject}.`;
    case "reject":
      return `Rejected ${subject}.`;
    case "unreject":
      return `Un-rejected ${subject}.`;
  }
}

// Stable module-level config (V2 requires stable prop references).
const PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * Oldest-waiting first, which is what the resolver already does for the pending
 * view — stated explicitly so the grid renders a sort indicator instead of
 * ordering rows by a rule the header does not show.
 *
 * The accepted view is left unsorted so the resolver's own ordering (pending
 * first, then longest-waiting) survives; it spans two columns and no single
 * header can express it.
 */
const PENDING_SORT_MODEL: GridSortModel = [{ field: "firstSeenAt", sort: "asc" }];
const SERVER_DEFAULT_SORT_MODEL: GridSortModel = [];

// Stacked cells — matched ID over match kind, decider over date. 38px clips both.
const ROW_HEIGHT = 56;

// "Decided by" is blank in the pending view, so it only earns its width once
// decided groups are on screen. Field stays `acceptedAt` — the visibility key.
const PENDING_COLUMN_VISIBILITY = { acceptedAt: false };
const DECIDED_COLUMN_VISIBILITY = { acceptedAt: true };

const getItems = (data: ExpiredAltIdMatchReviewsQuery) => data.expiredAltIdMatchReviews.items ?? [];
const getCount = (data: ExpiredAltIdMatchReviewsQuery) => data.expiredAltIdMatchReviews.count ?? 0;

/**
 * A checkbox rather than a toggle group: this widens the list rather than
 * performing an action, matching the Violations grid's "Unresolved only".
 */
function IncludeDecidedCheckbox({
  includeDecided,
  onChange,
}: {
  includeDecided: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <FormControlLabel
      sx={{ ml: 1, mr: 0, whiteSpace: "nowrap" }}
      control={
        <Checkbox
          size="small"
          checked={includeDecided}
          onChange={(event) => onChange(event.target.checked)}
        />
      }
      label={<Typography variant="body2">Include decided</Typography>}
    />
  );
}

export const ViolationMatchReview = () => {
  useBreadcrumb({ label: "Violation Match Review", to: "/staff/violation-match-review" });
  const alerts = useAlerts();
  const [includeDecided, setIncludeDecided] = useState(false);
  const [selected, setSelected] = useState<ReviewRow | null>(null);

  const [setAccepted, { loading: accepting }] = useMutation(SET_EXPIRED_ALT_ID_MATCH_ACCEPTED, {
    refetchQueries: ["ExpiredAltIdMatchReviews"],
  });
  const [setRejected, { loading: rejecting }] = useMutation(SET_EXPIRED_ALT_ID_MATCH_REJECTED, {
    refetchQueries: ["ExpiredAltIdMatchReviews"],
  });
  const saving = accepting || rejecting;

  const decide = useCallback(
    async (row: ReviewRow, decision: ReviewDecision) => {
      try {
        if (decision === "accept" || decision === "unaccept") {
          await setAccepted({ variables: { id: row.id, accepted: decision === "accept" } });
        } else {
          await setRejected({ variables: { id: row.id, rejected: decision === "reject" } });
        }
        alerts.success(decisionMessage(decision, row));
        // The refetch replaces the grid's rows but not the drawer's copy, so a
        // decision made in the drawer would leave a stale button behind it.
        setSelected(null);
      } catch (error) {
        alerts.error("Couldn't save that decision.", error);
      }
    },
    [alerts, setAccepted, setRejected]
  );

  const columns = useMemo(() => buildViolationMatchColumns(decide, saving), [decide, saving]);

  // Row click opens the group; the row's own Accept stays, so a staff member who
  // already knows the group can decide without opening anything.
  const onRowClick = useCallback((params: GridRowParams<ReviewRow>) => {
    setSelected(params.row);
  }, []);

  const commandButtons = useMemo<ReactNode[]>(
    () => [
      <IncludeDecidedCheckbox
        key="include-decided"
        includeDecided={includeDecided}
        onChange={setIncludeDecided}
      />,
    ],
    [includeDecided]
  );

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      search: omnisearch,
      pendingOnly: !includeDecided,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
    }),
    [includeDecided]
  );

  return (
    <Stack sx={{ pt: 3 }} spacing={2}>
      {/* Match the grid's own horizontal inset (OmnisearchDataGridV2 wraps in px: 3). */}
      <Typography variant="body2" color="text.secondary" sx={{ px: 3 }}>
        Violations matched to a facility only through an expired EPA or FRS ID. They stay hidden
        from the customer until you accept the match — or until someone corrects the ID&apos;s
        expiration on the facility profile, which releases them on the next weekly run. Reject a
        match that isn&apos;t this facility&apos;s to dismiss it from the queue.
      </Typography>
      <OmnisearchDataGridV2
        // Remount on the toggle so the grid refetches with the new variables
        // instead of holding the previous page's rows.
        key={includeDecided ? "all" : "pending"}
        query={EXPIRED_ALT_ID_MATCH_REVIEWS}
        getItems={getItems}
        getCount={getCount}
        persistenceKey="staff-violation-match-review"
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        initialSortModel={includeDecided ? SERVER_DEFAULT_SORT_MODEL : PENDING_SORT_MODEL}
        initialColumnVisibilityModel={
          includeDecided ? DECIDED_COLUMN_VISIBILITY : PENDING_COLUMN_VISIBILITY
        }
        columns={columns}
        commandButtons={commandButtons}
        buildVariables={buildVariables}
        rowHeight={ROW_HEIGHT}
        // The resolver's `search` is plain free text over customer, facility, and
        // ID. Leaving the filter popover on would offer key:value filters it
        // matches literally, so every filtered view would read as empty.
        showFilters={false}
        excludeFilterColumns={VIOLATION_MATCH_COLUMN_FIELDS}
        onRowClick={onRowClick}
        noRowsMessage="No expired-ID matches waiting on review."
      />
      <ViolationMatchDrawer
        row={selected}
        onClose={() => setSelected(null)}
        onDecide={decide}
        saving={saving}
      />
    </Stack>
  );
};
